import { useEffect, useRef, useState } from "react";

import { AbsentGlyph, Button, FailedGlyph, Dialog, Notice, PageFrame, ProblemBox, SignedOutCard, StripMark, TopBar } from "@don/design-system";
import type { ConnectMe, ConnectionView, LastAttempt, MailProvider, SuggestedProvider } from "@don/wire";

import { WORDMARK } from "../shell/ConsoleShell.js";
import { AdminDisclosure, InfoMark, WaitingMark } from "./AdminDisclosure.js";
import { ADMIN_COPY, COPY, OTHER, PROVIDER_COPY, PROVIDERS } from "./copy.js";
import {
  adminState, attemptProviderOf, beforeAttemptDisclosure, disconnectConnection, firstLoad, layout, offered, providerOf,
  type AdminState, type ConnectPort, type Disconnect, type PageState,
} from "./session.js";

/**
 * The Connections page (issue 392, epic 26-10-27-person-files-voice design-spec §2.1, §5.1):
 * the employee's one web page, mounted by `main.tsx` in place of the owner Console, so no owner
 * read fires and nothing redirects to `/sign-in`. `token` is what `takeToken` already took off
 * the URL before this mounted; `timedOut` is whether that URL was the api's `#timed-out`. It
 * shows connections only: nothing from `voice.md` or the build.
 */
export function ConnectApp({ port, token, timedOut = false }: { port: ConnectPort; token: string | null; timedOut?: boolean }) {
  const [state, setState] = useState<PageState>({ kind: "opening" });
  // One spend per page load: React's development double effect must not spend the link twice,
  // so the load is started once and each effect run only listens to it.
  const load = useRef<Promise<PageState> | null>(null);

  // Connect Gmail or Outlook: the button turns pending in this render, and the tab leaves for the
  // provider after the frame that draws it (design-spec §5.1 `page-consent-handoff`, Outlook
  // design-spec `page-consent-handoff-outlook`).
  const handingOff = state.kind === "ready" ? (state.handoff ?? false) : false;
  const via = state.kind === "ready" ? (state.via ?? "google") : "google";
  useEffect(() => {
    if (handingOff === false) return;
    const chooseAccount = handingOff === "choose";
    const start = via === "microsoft" ? port.startMicrosoft : port.startGoogle;
    const frame = requestAnimationFrame(() => start.call(port, { chooseAccount }));
    return () => cancelAnimationFrame(frame);
  }, [handingOff, via, port]);
  // A press leaves any disconnect result behind (the handoff frame is plain, §5.1).
  const connect = (choose = false, provider: MailProvider = "google") =>
    setState((now) =>
      now.kind === "ready" ? { ...now, handoff: choose ? "choose" : true, via: provider, disconnect: undefined } : now,
    );

  // Disconnect (issue 397): confirm, then the POST with Cancel disabled, then the one-shot result.
  const setDisconnect = (to: Disconnect | undefined, from?: Disconnect) =>
    setState((now) =>
      now.kind === "ready" && (from === undefined || now.disconnect === from) ? { ...now, disconnect: to } : now,
    );
  const disconnect: DisconnectActions = {
    ask: () => setDisconnect("confirm"),
    // Esc, the scrim and Cancel: only while the confirm is open, never while the POST runs.
    cancel: () => setDisconnect(undefined, "confirm"),
    confirm: () => {
      if (state.kind !== "ready" || state.disconnect !== "confirm") return;
      const me = state.me;
      setDisconnect("pending", "confirm");
      void disconnectConnection(port, me).then(setState);
    },
  };

  // Back from Google restores this page from the back/forward cache with the handoff frame still
  // drawn; put the button back (394 code-quality P1-4, `e2e/console/connect.spec.ts`).
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) setState((now) => (now.kind === "ready" ? { ...now, handoff: false } : now));
    };
    window.addEventListener("pageshow", restored);
    return () => window.removeEventListener("pageshow", restored);
  }, []);

  useEffect(() => {
    document.title = COPY.tabTitle;
    let live = true;
    load.current ??= firstLoad(port, token, timedOut);
    void load.current.then((next) => {
      if (live) setState(next);
    });
    return () => {
      live = false;
    };
  }, [port, token, timedOut]);

  return (
    <div
      data-connect={state.kind}
      style={{
        display: "flex", flexDirection: "column", height: "100%", minHeight: 0,
        background: "var(--ground)", color: "var(--ink-1)",
      }}
    >
      <ConnectScreen state={state} onConnect={connect} disconnect={disconnect} />
    </div>
  );
}

/** The Disconnect presses the page answers (issue 397). */
export interface DisconnectActions {
  ask: () => void;
  cancel: () => void;
  confirm: () => void;
}

/** A Connect press: "Use another account" asks for the chooser; `provider` is the row's. */
export type OnConnect = (chooseAccount?: boolean, provider?: MailProvider) => void;

export function ConnectScreen({
  state, onConnect, disconnect,
}: { state: PageState; onConnect?: OnConnect; disconnect?: DisconnectActions }) {
  switch (state.kind) {
    case "opening":
      return <SignedOutCard wordmark={WORDMARK} status={COPY.opening} hook="data-connect-card" />;
    case "link-expired":
      return <Expired title={COPY.linkExpiredTitle} body={COPY.linkExpiredBody} />;
    case "session-expired":
      return <Expired title={COPY.sessionExpiredTitle} body={COPY.sessionExpiredBody} />;
    case "failed":
      return (
        <SignedOutCard wordmark={WORDMARK} hook="data-connect-card">
          <CardBody lines={[COPY.failed]} />
        </SignedOutCard>
      );
    case "ready":
      return (
        <Connections
          me={state.me}
          handoff={(state.handoff ?? false) !== false ? (state.via ?? "google") : null}
          onConnect={onConnect}
          disconnect={state.disconnect ?? null}
          disconnected={state.disconnected ?? "google"}
          actions={disconnect}
        />
      );
  }
}

/** Title, mechanism, way back. No controls (ux-spec 7). */
function Expired({ title, body }: { title: string; body: string }) {
  return (
    <SignedOutCard wordmark={WORDMARK} title={title} hook="data-connect-card">
      <CardBody lines={[body, COPY.wayBack]} />
    </SignedOutCard>
  );
}

/** The card's body block: margin-top -12, gap 8, ink-2, the last line ink-1 (§4.1.1). */
function CardBody({ lines }: { lines: readonly string[] }) {
  return (
    <div
      style={{
        display: "flex", flexDirection: "column", gap: "var(--space-1)", marginTop: -12,
        fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)",
      }}
    >
      {lines.map((line, index) => (
        <p key={line} style={{ margin: 0, color: index === lines.length - 1 ? "var(--ink-1)" : "var(--ink-2)" }}>
          {line}
        </p>
      ))}
    </div>
  );
}

/** "Don", a divider, the strip mark and the page's `<h1>` (§2.1, §4.1.1, D14). */
function Heading() {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
      {WORDMARK}
      <span aria-hidden="true" style={{ display: "block", width: 1, height: 20, background: "var(--line)" }} />
      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <StripMark />
        <h1
          style={{
            margin: 0, fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)",
            fontWeight: "var(--type-body-weight-current)", letterSpacing: "var(--type-body-tracking)",
          }}
        >
          {COPY.header}
        </h1>
      </span>
    </span>
  );
}

function Connections({
  me, handoff, onConnect, disconnect, disconnected = "google", actions,
}: {
  me: ConnectMe;
  /** The provider whose handoff is in flight, or `null`. */
  handoff: MailProvider | null;
  onConnect?: OnConnect;
  disconnect: Disconnect | null;
  /** The provider a `done` Disconnect removed (issue 469). */
  disconnected?: MailProvider;
  actions?: DisconnectActions;
}) {
  // The held Connection's provider names the confirm and the failure (issue 469).
  const holding: MailProvider = me.connection !== null ? providerOf(me.connection) : "google";
  // Outlook's result adds who else can remove Don (PRD §2 "Revoke", decision 34).
  const removal =
    disconnected !== "microsoft"
      ? null
      : me.suggested_provider === "microsoft_personal"
        ? PROVIDER_COPY.disconnectedPersonal
        : PROVIDER_COPY.disconnectedAdmin;
  // After a Disconnect the sheet scrolls, instantly, so the last row clears the scroll area's
  // bottom by 32 (Outlook design-spec §4.1, decision 30).
  const scroller = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const area = scroller.current;
    if (disconnect !== "done" || area === null) return;
    const rowsDrawn = area.querySelectorAll("[data-connect-row]");
    const last = rowsDrawn[rowsDrawn.length - 1];
    if (last === undefined) return;
    const overflow = last.getBoundingClientRect().bottom + 32 - area.getBoundingClientRect().bottom;
    if (overflow > 0) area.scrollTop += overflow;
  }, [disconnect]);
  const providers = offered(me);
  // The guess orders the rows and picks the primary (issue 464); once a Connection exists only
  // its row shows (Outlook design-spec §5.1).
  const plan = layout(me);
  const rows: MailProvider[] = me.connection !== null ? [providerOf(me.connection)] : plan.rows;
  // A press leaves the outcome behind: the handoff frame is plain not connected (§5.1).
  const flash = handoff !== null ? null : (me.last_attempt ?? null);
  // The start route refused a second provider (issue 463, D8): a notice on top naming the provider
  // held, over its row, with no switch line and no close line. Every other outcome draws under the
  // row of the provider it names.
  const refused = flash?.outcome === "refused_one_connection" ? flash : null;
  const attempt = refused === null ? flash : null;
  const held: MailProvider | null =
    refused === null ? null : me.connection !== null ? providerOf(me.connection) : OTHER[attemptProviderOf(refused)];
  // W2: the mechanism, once, above the rows while nothing is connected. With Gmail alone a
  // problem replaces it, as it replaced epic A's row why line (390's drivers, unedited).
  const intro = me.connection === null && (rows.length > 1 || problemOf(attempt) === null);
  // The Outlook row's admin state and its closed disclosure before an attempt (issue 465).
  const admin = adminState(me);
  const before = admin === null && beforeAttemptDisclosure(me);
  return (
    <>
      <TopBar
        wordmark={<Heading />}
        tabs={[]}
        current={null}
        end={
          <span
            data-connect-person=""
            style={{
              fontSize: "var(--type-body-size)", fontWeight: "var(--type-body-weight-strong)", color: "var(--ink-2)",
            }}
          >
            {me.display_name}
          </span>
        }
      />
      <p
        data-connect-ephemeral=""
        style={{
          flex: "none", width: 624, maxWidth: "calc(100% - 80px)", margin: "-6px auto 0", padding: "0 0 10px",
          boxSizing: "border-box", fontSize: "var(--type-small-size)", lineHeight: "var(--type-small-line)",
          color: "var(--ink-2)",
        }}
      >
        {COPY.ephemeral}
      </p>
      <div ref={scroller} style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <PageFrame variant="narrow">
          {disconnect === "done" && (
            <div data-connect-result="disconnected" style={{ marginBottom: "var(--space-3)" }}>
              <Notice tone="absent">
                <p style={{ margin: 0 }}>{PROVIDER_COPY.disconnected(disconnected)}</p>
                {removal !== null && (
                  <p data-connect-removal="" style={{ ...SMALL, margin: "2px 0 0", color: "var(--ink-2)" }}>{removal}</p>
                )}
                <p style={{ ...SMALL, margin: 0, color: "var(--ink-2)" }}>{COPY.close}</p>
              </Notice>
            </div>
          )}
          {disconnect === "failed" && (
            <div data-connect-result="disconnect-failed" style={{ marginBottom: "var(--space-3)" }}>
              <Notice tone="failed">{PROVIDER_COPY.disconnectFailed(holding)}</Notice>
            </div>
          )}
          {held !== null && (
            <div data-connect-result="refused-one-connection" style={{ marginBottom: "var(--space-3)" }}>
              <Notice tone="info">
                <p style={{ margin: 0, fontWeight: 600 }}>{PROVIDER_COPY.refusedTitle(held)}</p>
                <p style={{ ...SMALL, margin: "2px 0 0", color: "var(--ink-2)" }}>{PROVIDER_COPY.refusedLine(held)}</p>
              </Notice>
            </div>
          )}
          {intro && (
            <p
              data-connect-intro=""
              style={{ ...SMALL, margin: "0 0 28px", maxWidth: "56ch", color: "var(--ink-2)" }}
            >
              {COPY.why}
            </p>
          )}
          {rows.map((provider, index) => (
            <ProviderRow
              key={provider}
              provider={provider}
              place={{ first: index === 0, last: index === rows.length - 1 }}
              // The guessed row's Connect, or epic A's single row's, is the one primary.
              primary={plan.primary === provider}
              guess={me.connection === null && plan.primary === provider ? plan.guess : null}
              connection={me.connection !== null && providerOf(me.connection) === provider ? me.connection : null}
              // The callback's outcome draws under the row of the provider it names (issue 463).
              attempt={attempt !== null && attemptProviderOf(attempt) === provider ? attempt : null}
              handoff={handoff === provider}
              admin={
                provider === "microsoft" && me.connection === null
                  ? { state: admin, before, link: me.admin_link ?? null }
                  : null
              }
              switchLine={providers.length > 1 && held === null}
              closeLine={held === null}
              onConnect={onConnect}
              onDisconnect={actions?.ask}
            />
          ))}
        </PageFrame>
      </div>
      {(disconnect === "confirm" || disconnect === "pending") && (
        <ConfirmDisconnect provider={holding} busy={disconnect === "pending"} actions={actions} />
      )}
    </>
  );
}

/**
 * The destructive confirm (design-spec §4.1.4, §5.1 `page-disconnect-confirm`): Cancel is the
 * primary and focused on open; Return on it, Esc and the scrim cancel. While the POST runs,
 * Disconnect reads "Disconnecting…" pending, Cancel is disabled and Esc and the scrim do nothing.
 */
function ConfirmDisconnect({
  provider, busy, actions,
}: { provider: MailProvider; busy: boolean; actions?: DisconnectActions }) {
  return (
    <Dialog
      title={PROVIDER_COPY.disconnectTitle(provider)}
      onClose={() => actions?.cancel()}
      destructive={{
        children: busy ? COPY.disconnecting : COPY.disconnectConfirm,
        pending: busy,
        disabled: busy,
        onClick: () => actions?.confirm(),
        "data-connect-disconnect-confirm": "",
      }}
      safe={{ children: COPY.disconnectCancel, disabled: busy, onClick: () => actions?.cancel(), "data-connect-disconnect-cancel": "" }}
    >
      {COPY.disconnectBody}
    </Dialog>
  );
}

/** "Oct 27, 2026": the Console's date format, in the browser's time zone (design-spec §6.1). */
export function sinceDate(ms: number): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(ms));
}

const SMALL = { fontSize: "var(--type-small-size)", lineHeight: "var(--type-small-line)" } as const;

/**
 * What the last callback left for a not-connected row (issue 395, design-spec §5.1): the
 * cancelled note under the full row, or a problem that replaces the why line and the primary.
 * `connected` (and anything on a connected row) draws nothing extra. A mismatch with no address
 * of the person's own cannot occur (ux-spec 48) and reads as connect failed.
 */
type Problem = { kind: "scopes" | "mismatch" | "failed"; lead: string; rest: string; action: string; choose: boolean };

export function problemOf(attempt: LastAttempt | null): Problem | null {
  if (attempt === null) return null;
  // Its provider's words (issue 463): Gmail's are epic A's, Outlook's design-spec §6.1.
  const p = attemptProviderOf(attempt);
  const failed: Problem = {
    kind: "failed", lead: PROVIDER_COPY.connectFailedLead(p), rest: PROVIDER_COPY.connectFailedRest(p),
    action: COPY.connectFailedAction, choose: false,
  };
  switch (attempt.outcome) {
    case "scopes_missing":
      // Outlook's partial grant is an admin problem with its own row state (issue 465, D9).
      if (p === "microsoft") return null;
      return { kind: "scopes", lead: PROVIDER_COPY.scopesLead(p), rest: COPY.scopesRest, action: COPY.scopesAction, choose: false };
    case "mismatch":
      if (attempt.tried_address !== null && attempt.expected_address !== null) {
        return {
          kind: "mismatch", lead: PROVIDER_COPY.mismatchLead(p),
          rest: PROVIDER_COPY.mismatchRest(p, attempt.tried_address, attempt.expected_address),
          action: COPY.mismatchAction, choose: true,
        };
      }
      return failed;
    case "failed":
      return failed;
    default:
      return null;
  }
}

/**
 * One provider's row (§4.1.3; Outlook design-spec W1, issue 462): Gmail's and Outlook's are this
 * one component, keyed by `provider`, its words from the provider table (`copy.ts`). The row
 * carries `data-connect-row="gmail|outlook"`; Gmail's Connect keeps `data-connect-gmail`, Outlook's
 * is `data-connect-outlook`. Every row but the first has a 1 px rule on top. With both providers
 * offered, a connected row gains the switch line (Outlook design-spec decision 8). The testing line
 * and the reconnect date are Google's only. An `active` Connection draws the connected row: "Connected as" and the
 * address at 500, "Since <date>", a quiet "Disconnect" (opens the confirm, issue 397) and, under the row,
 * "You can close this tab.". While a reconnect is due (`reconnect_by_ms`, Testing mode, issue 407)
 * the since line gains " · Reconnect by <date>", the testing line follows, a primary "Reconnect"
 * (the same handoff as Connect Gmail) sits after Disconnect, and the close line goes. A `lapsed`
 * Connection (issue 405, `page-lapsed`) draws the failed glyph, "Needs renewing" at 600, "·" and
 * the address, the testing line, then the same quiet Disconnect and primary Reconnect, and no close
 * line. Any other reads as not connected: the absent glyph, the why line
 * and a primary "Connect Gmail", which turns pending "Opening Google…" while the tab leaves for
 * Google. Only the Connection's fields are read: nothing about the voice (V1).
 */
function ProviderRow({
  provider, place, primary, guess, connection, attempt, handoff, admin = null, switchLine, closeLine = true, onConnect, onDisconnect,
}: {
  provider: MailProvider;
  place: { first: boolean; last: boolean };
  primary: boolean;
  /** The guess that put this row first (issue 464): its line under "Not connected". */
  guess: SuggestedProvider | null;
  connection: ConnectionView | null;
  attempt: LastAttempt | null;
  handoff: boolean;
  /**
   * Outlook with no Connection (issue 465): its admin state, whether the closed disclosure shows
   * before an attempt, and the note's admin link.
   */
  admin?: { state: AdminState | null; before: boolean; link: string | null } | null;
  switchLine: boolean;
  /** "You can close this tab." under a connected row; not under the one-Connection refusal (issue 463). */
  closeLine?: boolean;
  onConnect?: OnConnect;
  onDisconnect?: () => void;
}) {
  const words = PROVIDERS[provider];
  const google = provider === "google";
  const press = (choose = false) => onConnect?.(choose, provider);
  const connected = connection !== null && connection.state === "active" ? connection : null;
  const lapsed = connection !== null && connection.state === "lapsed" ? connection : null;
  // Sign-in rules lapsed Outlook (issue 468, design-spec S5): no Reconnect, the guide instead.
  const blockedLapse = lapsed !== null && !google && lapsed.lapse === "blocked";
  const reconnectBy = google ? (connected?.reconnect_by_ms ?? null) : null;
  const problem = connected ? null : problemOf(attempt);
  const cancelled = !connected && attempt?.outcome === "cancelled";
  const cancelledWords = attempt === null ? COPY.cancelled : PROVIDER_COPY.cancelled(attemptProviderOf(attempt));
  // An outcome box under the row (Outlook design-spec §4.1): margin-top 16 (epic A's single row
  // keeps its 20), and the row's 24 bottom padding moves below the box, before the next rule.
  const below = cancelled || problem !== null;
  const waiting = connection === null ? (admin?.state ?? null) : null;
  // The disclosure: open in an admin state, closed before an attempt with no problem (issue 465).
  const disclosure = blockedLapse
    ? "blocked"
    : connection !== null || admin === null ? null : waiting !== null ? waiting : admin.before && problem === null ? "before" : null;
  // An admin state's own action (Outlook design-spec §5.1): Try again (secondary when the
  // approval is required, primary while waiting), or a quiet Connect again for a partial grant.
  const adminAction =
    waiting === null || waiting === "blocked" ? null : (
      <Button
        variant={waiting === "awaiting" ? "primary" : waiting === "required" ? "secondary" : "quiet"}
        data-connect-outlook=""
        pending={handoff}
        disabled={handoff}
        onClick={() => press()}
      >
        {handoff ? PROVIDER_COPY.handoff(provider) : waiting === "scopes" ? ADMIN_COPY.scopesAction : ADMIN_COPY.tryAgain}
      </Button>
    );
  const alone = place.first && place.last;
  const boxSpacing = { marginTop: alone ? 20 : 16, marginBottom: place.last ? 0 : 24 };
  const reconnect = (
    <Button variant="primary" data-connect-reconnect="" pending={handoff} disabled={handoff} onClick={() => press()}>
      {handoff ? PROVIDER_COPY.handoff(provider) : COPY.reconnect}
    </Button>
  );
  const disconnect = (
    <Button variant="quiet" data-connect-disconnect="" onClick={() => onDisconnect?.()}>
      {COPY.disconnect}
    </Button>
  );
  const testing = (
    <p data-connect-testing="" style={{ ...SMALL, margin: 0, maxWidth: "46ch", color: "var(--ink-2)" }}>
      {COPY.testing}
    </p>
  );
  return (
    <>
      <div
        data-connect-row={words.row}
        style={{
          paddingTop: place.first ? 4 : 24, paddingBottom: place.last || below ? 0 : 24,
          borderTop: place.first ? "none" : "1px solid var(--line)",
        }}
      >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            style={{
              margin: 0, fontSize: "var(--type-row-title-size)", lineHeight: "var(--type-row-title-line)",
              fontWeight: "var(--type-row-title-weight)", letterSpacing: "var(--type-row-title-tracking)",
            }}
          >
            {words.name}
          </h2>
          {connected ? (
            <>
              <div data-connect-status="connected" style={{ ...SMALL, marginTop: 4, color: "var(--ink-1)" }}>
                {COPY.connectedAs}
                <span data-connect-address="" style={{ fontWeight: 500 }}>{connected.address}</span>
              </div>
              <div data-connect-since="" style={{ ...SMALL, color: "var(--ink-2)" }}>
                {COPY.since}
                {sinceDate(connected.connected_at_ms)}
                {reconnectBy !== null && (
                  <span data-connect-reconnect-by="">
                    {COPY.reconnectBy}
                    {sinceDate(reconnectBy)}
                  </span>
                )}
              </div>
              {reconnectBy !== null && testing}
            </>
          ) : lapsed ? (
            <>
              <div
                data-connect-status={blockedLapse ? "blocked" : "lapsed"}
                style={{ ...SMALL, display: "flex", alignItems: "center", gap: "var(--space-075)", marginTop: 4, color: "var(--ink-1)" }}
              >
                <FailedGlyph size={16} />
                <span style={{ fontWeight: 600 }}>{blockedLapse ? ADMIN_COPY.blockedStatus : COPY.lapsed}</span>
                <span style={{ color: "var(--ink-2)" }}>·</span>
                <span data-connect-address="">{lapsed.address}</span>
              </div>
              {google ? testing : !blockedLapse && (
                <p data-connect-why="" style={{ ...SMALL, margin: 0, maxWidth: "46ch", color: "var(--ink-2)" }}>
                  {PROVIDER_COPY.lapsedWhy(provider)}
                </p>
              )}
            </>
          ) : waiting === "blocked" ? (
            <div
              data-connect-status="blocked"
              style={{ ...SMALL, display: "flex", alignItems: "center", gap: "var(--space-075)", marginTop: 4, color: "var(--ink-1)" }}
            >
              <FailedGlyph size={16} />
              <span style={{ fontWeight: 600 }}>{COPY.notConnected}</span>
            </div>
          ) : waiting !== null ? (
            <>
              <div
                data-connect-status={`admin-${waiting}`}
                style={{ ...SMALL, display: "flex", alignItems: "center", gap: "var(--space-075)", marginTop: 4, color: "var(--ink-1)" }}
              >
                <span style={{ display: "flex", color: "var(--ink-2)" }}>
                  {waiting === "required" ? <InfoMark /> : waiting === "awaiting" ? <WaitingMark /> : <FailedGlyph size={16} />}
                </span>
                <span style={{ fontWeight: 500 }}>
                  {waiting === "required"
                    ? ADMIN_COPY.requiredStatus
                    : waiting === "awaiting"
                      ? ADMIN_COPY.waitingStatus
                      : ADMIN_COPY.scopesStatus}
                </span>
              </div>
              {waiting === "awaiting" && (
                <p data-connect-why="" style={{ ...SMALL, margin: "8px 0 0", maxWidth: "46ch", color: "var(--ink-2)" }}>
                  {ADMIN_COPY.waitingWhy}
                </p>
              )}
            </>
          ) : (
            <>
              <div
                data-connect-status="not-connected"
                style={{ ...SMALL, display: "flex", alignItems: "center", gap: "var(--space-075)", marginTop: 4, color: "var(--ink-1)" }}
              >
                <span style={{ display: "flex", color: "var(--ink-2)" }}>
                  <AbsentGlyph />
                </span>
                <span>{COPY.notConnected}</span>
              </div>
              {guess !== null && problem === null && (
                <p data-connect-guess={words.row} style={{ ...SMALL, margin: "4px 0 0", color: "var(--ink-2)" }}>
                  {guess === "microsoft_personal" ? PROVIDER_COPY.guessPersonal : PROVIDER_COPY.guess(provider)}
                </p>
              )}
            </>
          )}
        </div>
        <div style={{ flex: "none", display: "flex", alignItems: "center", gap: "var(--space-25)" }}>
          {connected ? (
            <>
              {disconnect}
              {reconnectBy !== null && reconnect}
            </>
          ) : lapsed ? (
            <>
              {disconnect}
              {!problem && !blockedLapse && reconnect}
            </>
          ) : adminAction !== null ? (
            adminAction
          ) : problem || waiting === "blocked" ? null : (
            <Button
              variant={primary ? "primary" : "secondary"}
              {...{ [`data-connect-${words.row}`]: "" }}
              pending={handoff}
              disabled={handoff}
              onClick={() => press()}
            >
              {handoff ? PROVIDER_COPY.handoff(provider) : PROVIDER_COPY.connect(provider)}
            </Button>
          )}
        </div>
      </div>
      {disclosure !== null && <AdminDisclosure key={disclosure} kind={disclosure} link={admin?.link ?? null} />}
      </div>
      {cancelled && (
        <div data-connect-attempt="cancelled" style={boxSpacing}>
          <Notice tone="info">{cancelledWords}</Notice>
        </div>
      )}
      {problem && (
        <div data-connect-attempt={problem.kind} style={boxSpacing}>
          <ProblemBox wide retryLabel={problem.action} onRetry={() => press(problem.choose)}>
            <span style={{ display: "block", fontWeight: "var(--type-body-weight-current)" }}>{problem.lead}</span>
            <span style={{ display: "block" }}>{problem.rest}</span>
          </ProblemBox>
        </div>
      )}
      {switchLine && (connected || lapsed) && (
        <p data-connect-switch="" style={{ ...SMALL, margin: "20px 0 0", color: "var(--ink-2)" }}>
          {PROVIDER_COPY.switchLine(provider)}
        </p>
      )}
      {connected && reconnectBy === null && closeLine && (
        <p data-connect-close="" style={{ ...SMALL, margin: "20px 0 0", color: "var(--ink-2)" }}>
          {COPY.close}
        </p>
      )}
    </>
  );
}
